/**
 * Permissions Section
 * Checkbox groups for API permissions with presets
 */

import { UseFormReturn } from 'react-hook-form';
import { PackageFormValues } from '@/src/schemas/admin/package-schema';
import { AVAILABLE_PERMISSIONS, PERMISSION_PRESETS } from '@/src/constants/admin/packages';
import {
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface PermissionsSectionProps {
  form: UseFormReturn<PackageFormValues>;
}

export default function PermissionsSection({ form }: PermissionsSectionProps) {
  const permissions = form.watch('permissions') || [];

  const togglePermission = (permission: string) => {
    const currentPermissions = form.getValues('permissions') || [];
    const newPermissions = currentPermissions.includes(permission)
      ? currentPermissions.filter(p => p !== permission)
      : [...currentPermissions, permission];
    
    console.log('🔧 [PermissionsSection] togglePermission:', {
      permission,
      before: currentPermissions,
      after: newPermissions
    });
    
    form.setValue('permissions', newPermissions, { shouldDirty: true, shouldValidate: true });
  };

  const applyPreset = (presetKey: string) => {
    const preset = PERMISSION_PRESETS[presetKey as keyof typeof PERMISSION_PRESETS];
    if (preset) {
      form.setValue('permissions', [...preset]);
    }
  };

  const selectAll = () => {
    const allPermissions = AVAILABLE_PERMISSIONS.flatMap(cat => cat.permissions);
    form.setValue('permissions', allPermissions);
  };

  const deselectAll = () => {
    form.setValue('permissions', []);
  };

  return (
    <div className="space-y-6">
      {/* Preset Selector */}
      <div className="space-y-2">
        <label className="text-sm font-medium">ชุดสิทธิ์เริ่มต้น</label>
        <Select onValueChange={applyPreset}>
          <SelectTrigger>
            <SelectValue placeholder="เลือกชุดสิทธิ์ (ไม่บังคับ)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="starter">Starter - UI + Business Basics (8 permissions)</SelectItem>
            <SelectItem value="basic">Basic - API Platform Access (9 permissions)</SelectItem>
            <SelectItem value="premium">Premium - API Verification + Business (13 permissions)</SelectItem>
            <SelectItem value="enterprise">Enterprise - Full API + Advanced (15 permissions)</SelectItem>
            <SelectItem value="owner">Owner - Complete System (16 permissions)</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-sm text-muted-foreground">
          เลือกชุดสิทธิ์เริ่มต้น (สามารถแก้ไขเพิ่มเติมได้)
        </p>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" onClick={selectAll}>
          เลือกทั้งหมด
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={deselectAll}>
          ยกเลิกทั้งหมด
        </Button>
        <span className="ml-auto text-sm text-muted-foreground self-center">
          เลือกแล้ว: {permissions.length} รายการ
        </span>
      </div>

      {/* Permissions by Category */}
      <FormField
        control={form.control}
        name="permissions"
        render={() => (
          <FormItem>
            <div className="space-y-4">
              {AVAILABLE_PERMISSIONS.map((category) => (
                <div key={category.category} className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-3">{category.category}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {category.permissions.map((permission) => (
                      <div key={permission} className="flex items-center space-x-2">
                        <Checkbox
                          id={permission}
                          checked={permissions.includes(permission)}
                          onCheckedChange={() => togglePermission(permission)}
                        />
                        <label
                          htmlFor={permission}
                          className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          {permission}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      {permissions.length === 0 && (
        <div className="text-center py-4 border-2 border-dashed rounded-lg bg-muted/20">
          <p className="text-sm text-muted-foreground">
            ยังไม่มีสิทธิ์ที่เลือก กรุณาเลือกอย่างน้อย 1 รายการ
          </p>
        </div>
      )}
    </div>
  );
}
